import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  HelpCircle,
  Loader2,
  Mic2,
  Send,
  Sparkles,
  Volume2,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";
import Recorder from "./recorder";
import ResultBox from "./ResultBox";
import SpacingBox from "./Spacing";
import AudioPlayer from "./AudioPlayer";
import useFetchData from "../fetchpostData";
import ScoreBox from "./ResponseBox";
import Notification from "./Notification";
import Timers from "../PageComponent/Timers";

const PracticeShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

const DEFAULT_SHORT_ANSWER_RECORDING_SECONDS = 3;
const SHORT_ANSWER_ARTICLES = new Set(["a", "an", "the"]);
const SHORT_ANSWER_FILLERS = new Set(["uh", "um", "umm", "ah", "er", "erm", "hmm"]);

const normalizeShortAnswer = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(
      (word) =>
        word &&
        !SHORT_ANSWER_ARTICLES.has(word) &&
        !SHORT_ANSWER_FILLERS.has(word)
    )
    .join(" ");

const splitAcceptedShortAnswers = (answerText) =>
  String(answerText || "")
    .split(/\s*(?:,|;|\/|\||\bor\b)\s*/i)
    .map(normalizeShortAnswer)
    .filter(Boolean)
    .filter((answer, index, list) => list.indexOf(answer) === index);

const shortAnswerMatchesKey = (candidateAnswer, acceptedAnswer) => {
  const candidate = normalizeShortAnswer(candidateAnswer);
  const accepted = normalizeShortAnswer(acceptedAnswer);
  if (!candidate || !accepted) return false;
  if (candidate === accepted) return true;

  const candidateWords = candidate.split(" ");
  const acceptedWords = accepted.split(" ");
  if (acceptedWords.length === 1) return candidateWords.includes(accepted);

  const candidateSet = new Set(candidateWords);
  const overlap = acceptedWords.filter((word) => candidateSet.has(word)).length;
  return candidate.includes(accepted) || overlap / acceptedWords.length >= 0.9;
};

const getCorrectAnswerDisplay = (savedPractice, fallbackAnswer) => {
  const acceptedAnswers =
    savedPractice?.answerShortQuestionScore?.accepted_answers || [];

  if (fallbackAnswer) return fallbackAnswer;
  if (Array.isArray(acceptedAnswers) && acceptedAnswers.length > 0) {
    return acceptedAnswers.join(", ");
  }

  return "";
};

export default function AnswerShortQuestionPage(props) {
  const { title } = useParams();
  const section = "answershortquestions";
  const practiceItemId = title;
  const post = useFetchData(section, title);

  const [originalWords, setOriginalWords] = useState("");
  const [audioLink, setAudioLink] = useState("");
  const [text, setText] = useState("");
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [questionTitle, setQuestionTitle] = useState("");
  const [savedPractice, setSavedPractice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [result, setResult] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [recorderStartSignal, setRecorderStartSignal] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(
    DEFAULT_SHORT_ANSWER_RECORDING_SECONDS
  );

  useEffect(() => {
    if (post && post.media_link) {
      setOriginalWords(post.answer || "");
      setQuestionTitle(post.q_title || "Answer short question");
      setRecordedBlob(null);
      setSubmitted(false);
      setSavedPractice(null);
      setRecordingDuration(DEFAULT_SHORT_ANSWER_RECORDING_SECONDS);

      const fileName = encodeURIComponent(post.media_link.split("/").pop());

      fetch(`/ptedata/ptemedia/${fileName}`, { credentials: "include" })
        .then((response) => response.json())
        .then((data) => {
          if (data.url) {
            setAudioLink(data.url);
          } else {
            console.error("Failed to fetch audio URL");
          }
        })
        .catch((error) => console.error("Error fetching signed URL:", error));
    }
  }, [post]);

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-answershortquestion", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(practiceData),
      });

      if (!response.ok) {
        throw new Error("Failed to save practice data");
      }

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const highlightMatchingWords = () => {
      const acceptedAnswers = splitAcceptedShortAnswers(originalWords);
      const responseMatchesKey = acceptedAnswers.some((answer) =>
        shortAnswerMatchesKey(text, answer)
      );
      const words2 = text.replace(/\./g, " .").split(/\s+/).filter(Boolean);

      setResult(
        words2.map((word2, index) => (
          <span
            key={`${word2}-${index}`}
            style={{
              backgroundColor: responseMatchesKey ? "#CBFB8A" : "#E74C3C",
              marginRight: "4px",
            }}
          >
            {word2}
          </span>
        ))
      );
    };

    highlightMatchingWords();
  }, [originalWords, text]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recordedBlob) {
      setMessage("Please record audio first before submitting.");
      setNotificationType("error");
      setShowNotification(true);
      return;
    }

    setLoading(true);

    try {
      const audioForm = new FormData();
      audioForm.append("audio", recordedBlob, "recording.wav");

      const uploadRes = await fetch("/api/upload-audio", {
        method: "POST",
        body: audioForm,
      });

      if (!uploadRes.ok) {
        throw new Error("Audio upload failed");
      }

      const uploadData = await uploadRes.json();

      if (!uploadData.audioUrl) {
        throw new Error("Audio URL missing from upload response");
      }

      const transcribeForm = new FormData();
      transcribeForm.append("audio", recordedBlob, "recording.wav");

      const transRes = await fetch("/api/transcribe", {
        method: "POST",
        body: transcribeForm,
      });

      if (!transRes.ok) {
        throw new Error("Transcription request failed");
      }

      const transData = await transRes.json();

      if (!transData.text) {
        throw new Error("Transcription failed to return text");
      }

      const transcribedText = transData.text;
      setText(transcribedText);

      const practiceData = {
        practiceType: section,
        practiceItemId,
        response: {
          questionId: practiceItemId,
          audioUrl: uploadData.audioUrl,
          audioDuration: uploadData.duration || transData.duration || 0,
          answer: transcribedText,
          transcript: transData.transcript || transcribedText,
          words: transData.words || [],
          transcriptionEngine: transData.engine,
          isAttempted: true,
        },
      };

      const result = await savePractice(practiceData);

      setMessage("Practice data saved successfully!");
      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true);

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
        setSubmitted(true);
      }, 1000);
    } catch (error) {
      console.error("Submit error:", error);
      setMessage(error.message || "Something went wrong.");
      setNotificationType("error");
      setShowNotification(true);

      setLoading(false);
      setSubmitted(false);
    }
  };

  return (
    <PracticeShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Speaking practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {questionTitle || "Answer short question"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Listen to the question and answer with a short, direct response.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {isAttempted ? "Attempted" : "Not attempted"}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <HelpCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Listen and answer briefly
                </h2>
              </div>
            </div>

            <div className="rounded-md bg-slate-50 p-4 text-sm leading-6 text-gray-600">
              You will hear a question. Please give a short answer, usually one
              word or a short phrase. Keep your response clear and immediate.
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                  <Volume2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Audio prompt
                  </p>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Question audio
                  </h2>
                </div>
              </div>

              {audioLink ? (
                <AudioPlayer
                  audioSrc={audioLink}
                  onDurationChange={(duration) =>
                    setRecordingDuration(Math.max(1, Math.ceil(duration)))
                  }
                  onEnded={() =>
                    setRecorderStartSignal((currentSignal) => currentSignal + 1)
                  }
                />
              ) : (
                <div className="flex min-h-[220px] items-center justify-center rounded-md bg-slate-50 text-sm font-semibold text-gray-500 ring-1 ring-slate-100">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
                  Loading audio
                </div>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                    <Mic2 className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase text-purple-700">
                      Recording
                    </p>
                    <h2 className="text-xl font-extrabold text-gray-900">
                      Your answer
                    </h2>
                  </div>
                </div>
                {recordedBlob && (
                  <span className="inline-flex items-center gap-2 rounded-md bg-green-50 px-2.5 py-1 text-xs font-bold uppercase text-green-700 ring-1 ring-green-100">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Recorded
                  </span>
                )}
              </div>

              <Recorder
                key={`${practiceItemId}-${recordingDuration}`}
                text={text}
                setText={setText}
                {...props}
                onRecordingComplete={(blob) => setRecordedBlob(blob)}
                autoStart={false}
                autoStartSignal={recorderStartSignal}
                waitingLabel="Recording starts after question"
                duration={recordingDuration}
                className="mt-0 mb-0 w-full max-w-xl border border-slate-200 bg-slate-50 shadow-sm"
              />
            </div>
          </section>

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  Submit after recording to upload audio and generate feedback.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Submit
                </>
              )}
            </button>
          </section>

          {submitted && (
            <section className="space-y-5">
              <ResultBox result={result} />
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
                <div className="grid gap-0 lg:grid-cols-[minmax(260px,0.9fr),1.1fr]">
                  <div className="bg-violet-600 p-6 text-white">
                    <p className="text-sm font-semibold uppercase text-violet-100">
                      Total score
                    </p>
                    <h3 className="mt-1 text-2xl font-extrabold">
                      Content only
                    </h3>
                    <div className="mt-8 flex items-end gap-2">
                      <span className="text-6xl font-extrabold leading-none">
                        {savedPractice?.savedResponse?.totalScore ?? "N/A"}
                      </span>
                      <span className="pb-2 text-sm font-bold text-violet-100">
                        / {savedPractice?.maxScores?.overall || 1}
                      </span>
                    </div>
                    <div className="mt-6 rounded-md bg-white/10 p-4">
                      <p className="text-xs font-semibold uppercase text-violet-100">
                        Remarks
                      </p>
                      <p className="mt-1 text-xl font-extrabold">
                        {savedPractice?.savedResponse?.remarks ||
                          "Awaiting feedback"}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <p className="text-sm font-semibold uppercase text-purple-700">
                      Accepted answer check
                    </p>
                    <h4 className="mt-1 text-xl font-extrabold text-gray-900">
                      Content score
                    </h4>
                    <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-bold text-gray-800">Content</span>
                        <span className="text-lg font-extrabold text-gray-900">
                          {savedPractice?.savedResponse?.contentScore ?? "N/A"}/
                          {savedPractice?.maxScores?.content || 1}
                        </span>
                      </div>
                      <p className="mt-3 text-sm text-gray-600">
                        {
                          savedPractice?.answerShortQuestionScore?.feedback
                            ?.content
                        }
                      </p>
                    </div>

                    {getCorrectAnswerDisplay(savedPractice, originalWords) && (
                      <div className="mt-4 rounded-md border border-green-100 bg-green-50 p-4">
                        <p className="text-xs font-semibold uppercase text-green-700">
                          Correct answer
                        </p>
                        <p className="mt-1 text-base font-extrabold text-green-900">
                          {getCorrectAnswerDisplay(savedPractice, originalWords)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Answer what is asked, not a full sentence unless needed.
                  Short, accurate responses score better here.
                </p>
              </div>
            </div>
          </section>

          <ScoreBox
            originalWords={originalWords}
            practiceItemId={practiceItemId}
            onIsAttempted={setIsAttempted}
            showCorrectAnswers
          />
        </aside>
      </div>

      <SpacingBox />

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={() => setShowNotification(false)}
        />
      )}
    </PracticeShell>
  );
}
